import { useEffect, useRef, useState } from 'react';
import { color } from '../theme.js';

/**
 * A smoothly-ticking local countdown for a turn-game move clock.
 *
 * The server only re-sends `moveMsLeft` on a state push (a move, or reconnect),
 * so we seed a local deadline from it and tick down each frame; a new board
 * (boardSig changes) or a fresh server value re-seeds it. Losing the clock is a
 * loss server-side, so this is purely the visible urgency cue.
 */
export function useMoveCountdown(active: boolean, serverMsLeft: number, boardSig: string): number {
  const [secs, setSecs] = useState(active ? Math.ceil(serverMsLeft / 1000) : 0);
  const deadline = useRef(0);

  // The server re-sends `moveMsLeft` every tick (self-healing), so keep the
  // deadline continuously refined WITHOUT tearing down the interval each tick.
  // Only re-seed when the turn flips or the board changes.
  useEffect(() => {
    deadline.current = Date.now() + serverMsLeft;
  }, [serverMsLeft]);

  useEffect(() => {
    if (!active) { setSecs(0); return; }
    deadline.current = Date.now() + serverMsLeft;
    setSecs(Math.ceil(serverMsLeft / 1000));
    const id = window.setInterval(() => {
      setSecs(Math.ceil(Math.max(0, deadline.current - Date.now()) / 1000));
    }, 200);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, boardSig]);

  return secs;
}

/**
 * The move-clock header: "YOUR MOVE" with a draining bar + seconds, or
 * "RIVAL'S MOVE" while you wait. Urgent (red) in the final seconds.
 */
export function TurnMoveTimer({
  yourTurn,
  moveMsLeft,
  moveCapMs,
  boardSig,
}: {
  yourTurn: boolean;
  moveMsLeft: number;
  moveCapMs: number;
  boardSig: string;
}) {
  const secsLeft = useMoveCountdown(yourTurn, moveMsLeft, boardSig);
  const frac = moveCapMs > 0 ? Math.max(0, Math.min(1, (secsLeft * 1000) / moveCapMs)) : 0;
  const urgent = yourTurn && secsLeft <= 2;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 22 }}>
      <span style={{ fontSize: 11, letterSpacing: '.14em', fontWeight: 700, color: yourTurn ? (urgent ? color.redBright : color.lime) : color.cyan }}>
        {yourTurn ? 'YOUR MOVE' : 'RIVAL’S MOVE'}
      </span>
      <div style={{ flex: 1, height: 6, borderRadius: 999, background: 'rgba(255,255,255,.08)', overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${(yourTurn ? frac : 1) * 100}%`,
            borderRadius: 999,
            background: yourTurn ? (urgent ? color.redBright : color.lime) : 'rgba(34,224,255,.5)',
            transition: 'width .2s linear',
          }}
        />
      </div>
      {yourTurn && (
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13, fontWeight: 800, color: urgent ? color.redBright : 'rgba(234,255,234,.75)', minWidth: 16, textAlign: 'right' }}>
          {secsLeft}
        </span>
      )}
    </div>
  );
}
